import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Project from '../../../models/Project';
import { getFilteredSimpleIndicators } from '../../PortfolioDetail/PortfolioProjectsDetailContent/PortfolioProjectsDetailContent';
import ProjectsInPreparationTable from '../ProjectsInPreparationTable/ProjectsInPreparationTable';
import { getDynamicTableFormat, parseFilterToApiCall } from '../../../services/util';
import { ProjectCounter } from '../ProjectCounter/ProjectCounter';
import IndicatorDetailFilter from '../../../models/IndicatorDetailFilter';
import { ProjectsListSkeleton } from '../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { useTranslation } from 'react-i18next';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';

type Props = {
  currentIndicator: any;
  indicatorKey: string;
};

const ProjectsInPreparation = (props: Props) => {
  const dispatch = useDispatch();
  const { indicatorKey, currentIndicator } = props;
  const indicatorProjects = useSelector<any, Project[]>(state => state.indicatorProjects);
  const { projectTypeFilterOnDetail } = useSelector<any, IndicatorDetailFilter>(state => state.indicatorDetailFilter);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { t } = useTranslation();

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);

  const effectiveProjectTypeFilters =
    Array.isArray(filterIndicators.common.projectTypeFilters) && filterIndicators.common.projectTypeFilters.length > 0
      ? filterIndicators.common.projectTypeFilters
      : Array.isArray(stateProjectProductTypes) && stateProjectProductTypes.length > 0
        ? stateProjectProductTypes
        : [];
  const parsedProjectProductTypes = parseFilterToApiCall(filterIndicators.common.projectTypeFilters).projectProductTypes;

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: effectiveProjectTypeFilters },
    projectTypeFilters: parsedProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: parsedProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PROJECTS_IN_PREPARATION')));
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    getFilteredSimpleIndicators(
      dispatch,
      currentIndicator.id,
      currentIndicator.indicatorKey,
      setLoadingData,
      setError,
      projectTypeFilterChange,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator.id, dispatch, projectTypeFilterOnDetail, currentIndicator]);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange();

      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  return (
    <>
      {!isLoading ? (
        <div className="portfolio-projects-detail-content-container">
          <div className={indicatorProjects.length === 0 ? 'wrapper' : 'wrapper adjust-height'}>
            <ProjectsInPreparationTable
              projects={indicatorProjects}
              tableInfo={getDynamicTableFormat(indicatorKey)}
              loading={isLoading}
            />
            <ProjectCounter arrayToCount={indicatorProjects} countProjects={true} indicatorKey={indicatorKey} />
          </div>
        </div>
      ) : (
        <ProjectsListSkeleton />
      )}
    </>
  );
};

export default ProjectsInPreparation;
